import { PatientWithAccessRightDTO } from '@peartherapeutics/weaver-api-client';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';

import { History } from 'history';

import { getPatientDetailSuccess } from 'actions/currentPatient';
import pearClient from 'api/pearClient';
import CircularLoader from 'components/common/CircularLoader';
import ErrorInterceptor from 'components/common/ErrorInterceptor';
import { sorting } from 'utils/advancedSort';
import { programs } from 'utils/constants';
import Program from 'utils/program';
import { isSupportedProgram, routes } from 'utils/routeHelper';
import { hasRequiredScope, scopes } from 'utils/scope';

import Row from './Row';
import TableHeader from './TableHeader';
import './styles.scss';

interface IOwnProps {
  history: History;
  sortProp: Nullable<string>;
  sortOrder: Nullable<string>;
  sortBy: (prop: string) => void;
  patients: PatientWithAccessRightDTO[];
  isLoading: boolean;
  auth: IAuthenticateState;
  currentUserScopes: string[];
}

interface IDispatchProps {
  getPatientDetailSuccess: (patient: PatientWithAccessRightDTO) => void;
}

type PatientsTableType = IOwnProps & IDispatchProps;

const PatientsTable = (props: PatientsTableType) => {
  const {
    patients,
    sortProp,
    sortOrder,
    sortBy,
    isLoading,
    currentUserScopes,
    history,
    getPatientDetailSuccess,
  } = props;

  const [sortedPatients, setSortedPatients] = React.useState(
    [...patients].sort(sorting(sortProp, sortOrder)),
  );

  const hasPatientReadPermission = hasRequiredScope(currentUserScopes)(
    scopes.PATIENT_READ,
  );

  const handleOnClick = async (patient: PatientWithAccessRightDTO) => {
    if (!hasPatientReadPermission) return;
    try {
      Program.set(patient.currentAccessRight!.program);

      if (!isSupportedProgram(Program.get()?.name)) {
        Program.clear();
        history.push(routes.INVALID_PATH);
        return;
      }
      pearClient.v2.tracking.trackSelectedPatientEvent(patient.id);

      const isSomrystProgram = Program.get().name === programs.SOMRYST;
      getPatientDetailSuccess(patient);

      // requires updating and releasing everytime for each Program MFE
      if (isSomrystProgram) {
        history.push(routes.SOMRYST_DASHBOARD);
      } else {
        history.push(routes.PATIENT_DASHBOARD_IFRAME);
      }
    } catch (e) {
      ErrorInterceptor.error(e);
    }
  };

  const updatePatientData = (patientData: PatientWithAccessRightDTO) => {
    const patientIndex = sortedPatients.findIndex(
      (p) => p.id === patientData.id,
    );
    sortedPatients[patientIndex] = patientData;
    setSortedPatients([...sortedPatients]);
  };

  useEffect(() => {
    setSortedPatients([...patients].sort(sorting(sortProp, sortOrder)));
  }, [patients, sortProp, sortOrder]);

  const isNoResult = !isLoading && patients.length === 0;

  return (
    <table styleName="wrapper" data-testid="patient-summary-table">
        <TableHeader
          sortProp={sortProp}
          sortOrder={sortOrder}
          sortBy={sortBy}
          hasPatientReadPermission={hasPatientReadPermission}
        />
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan={10}>
                <CircularLoader />
              </td>
            </tr>
          ) : (
            sortedPatients.map((patient) => (
              <Row
                handleOnClick={() => handleOnClick(patient)}
                key={patient.id}
                patient={patient}
                updatePatientData={updatePatientData}
                actions={{ hasPatientReadPermission: hasPatientReadPermission }}
              />
            ))
          )}
          {isNoResult && (
            <tr>
              <td colSpan={10}>
                <div styleName="no-result">No results found</div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
  );
};

const mapStateToProps = (state: IRootStateType) => {
  return {
    auth: state.authenticate,
    currentUserScopes: state.authenticate?.profile?.scopes ?? [],
  };
};

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      getPatientDetailSuccess,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(PatientsTable);
